import React, { FunctionComponent, useState } from 'react';
import {
  Button,
  Col,
  Container,
  Row,
  InputGroup,
  Form as FormInput,
  Modal,
} from 'react-bootstrap';
import { DateTime } from 'luxon';
import { connect, ConnectedProps } from 'react-redux';
import { ApplicationState } from '../redux/States';
import apis from '../apis';
import { Student, StudentWithGradeNo } from '../model';
import {
  AuthedLayout,
  EditableTableDiv,
  HeaderDisplayKeysWithType,
} from '../components';
import { ErrorDispatches } from '../redux/Dispatches';
import { WrapClassBaseLayout, ClassSem } from '../layouts';
import { toast } from 'react-toastify';

const headerDisplayKeys: HeaderDisplayKeysWithType<StudentWithGradeNo>[] = [
  { display: '統編', property: 'pid' },
  { display: '年級', property: 'grade' },
  {
    display: '班級',
    property: 'className',
    onRender: (value: unknown) => {
      const num = parseInt(value as string, 10);
      return !isNaN(num) && num > 100 && num < 200 ? num - 100 : value;
    },
  },
  { display: '學生', property: 'name' },
  { display: '學號', property: 'sid' },
  { display: '性別', property: 'sex' },
  { display: '座號', property: 'seat' },
  {
    display: '生日',
    property: 'birth',
    onRender: (v) => (v instanceof DateTime ? v.toFormat('yyyy/M/d') : null),
  },
  { display: '父親', property: 'dad' },
  { display: '母親', property: 'mom' },
  { display: '監護人', property: 'guardian' },
  { display: '電話', property: 'tel' },
  { display: '郵區', property: 'zip' },
  { display: '住址', property: 'address' },
  { display: '緊急聯絡', property: 'erTel' },
  {
    display: '實歲',
    property: 'birth',
    onRender: (v) => {
      if (v instanceof DateTime) {
        const now = DateTime.now();
        let age = now.year - v.year;
        if (now.month < v.month || (now.month === v.month && now.day < v.day)) {
          age--;
        }
        return age;
      }
      return '';
    },
  },
  { display: '血型', property: 'blood' },
];

const mapStates = (state: ApplicationState, ownProps: ClassSem) => ({
  ...ownProps,
  ...state.auth,
});
const mapDispatches = ErrorDispatches;
const connector = connect(mapStates, mapDispatches);
type Props = ConnectedProps<typeof connector>;

const StudentSearchPage: FunctionComponent<Props> = ({
  user,
  catchErrorForModal,
}) => {
  const [students, setStudents] = useState<StudentWithGradeNo[]>([]);
  const [deleteTransfer, setDeleteTransfer] = useState<boolean>(false);
  const [deleteOtherSchooldData, setDeleteOtherSchooldData] =
    useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [disappearPid, setDisappearPid] = useState('');
  const [modalContent, setModalContent] = useState<string>();
  const [modalTitle, setModalTitle] = useState<string>();
  function handleSearch(type: 'name' | 'pid' | 'birth') {
    toast
      .promise(apis.getClassStudentsFilter(type, searchTerm.trim()), {
        pending: '資料載入中......',
        success: '資料載入成功！',
        error: '資料載入失敗！請查看錯誤資訊。',
      })
      .then((result) => {
        setStudents(result);
      })
      .catch(catchErrorForModal);
  }

  function updateStudentTransferDone() {
    if (disappearPid) {
      apis
        .updateStudentTransferDone(disappearPid)
        .then((result) => {
          setDisappearPid('');
          setDeleteTransfer(false);
          setModalTitle('確認為本校生');
          setModalContent('設定成功，請到基本資料確認');
        })
        .catch(catchErrorForModal);
    } else {
      toast.error('請輸入身分證');
    }
  }
  function updateOtherSchooldData() {
    if (disappearPid) {
      apis
        .updateOtherSchooldData(disappearPid)
        .then((result) => {
          setDisappearPid('');
          setDeleteOtherSchooldData(false);
          setModalTitle('移除非本校資料');
          setModalContent('移除成功，請到學生健康資料確認');
        })
        .catch(catchErrorForModal);
    } else {
      toast.error('請輸入身分證');
    }
  }
  console.log(user.name);
  return (
    <AuthedLayout>
      <Row className="mb-3 align-items-center">
        <Col xs={12} sm="auto" className="mb-2 pr-2">
          <FormInput.Control
            placeholder="輸入姓名 / 身分證 / 生日"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col xs="auto" className="mb-2 pr-2">
          <Button variant="primary" onClick={() => handleSearch('name')}>
            姓名尋找
          </Button>
        </Col>
        <Col xs="auto" className="mb-2 pr-2">
          <Button variant="secondary" onClick={() => handleSearch('pid')}>
            身分證座號<b className="text-warning">(全碼,區分大小寫)</b>尋找
          </Button>
        </Col>
        <Col xs="auto" className="mb-2 pr-2">
          <Button variant="info" onClick={() => handleSearch('birth')}>
            生日尋找
          </Button>
        </Col>
        <Col className="text-muted align-self-center">日期格式：1999/01/01</Col>
      </Row>
      {user.name == 'SHIS測試帳號' ? (
        <>
          {/* 適用於transferDone=0的學生 */}
          <Row className="mb-2">
            若找基本資料找不到學生，健康資料尋找有資料，請點選按鈕
            <Button
              className="mx-2"
              onClick={() => {
                setModalTitle('確認為本校生');
                setDeleteTransfer(true);
              }}
            >
              確認為本校生
            </Button>
            {/* (update studentTransfer transferDone = 1，delete same school rank data yearStudnetClass schoolId=99999) */}
          </Row>
          {/* 適用於多筆yearStudentClass的學生 */}
          <Row className="mb-4">
            <span>若出現非本校資料無法修改，請點選按鈕</span>
            <Button
              className="mx-2"
              onClick={() => {
                setModalTitle('移除非本校資料');
                setDeleteOtherSchooldData(true);
              }}
            >
              移除非本校資料
            </Button>
            {/* (delete same school rank yearStudnetClass) */}
          </Row>
        </>
      ) : (
        <></>
      )}

      <EditableTableDiv
        editable={true}
        deleteable={false}
        fixCol={3}
        headers={headerDisplayKeys}
        values={students}
        onEdit={(student) => {
          if (student?.pid) {
            window.location.href = `/student/phi?id=${encodeURIComponent(
              student.pid
            )}`;
          }
        }}
      />
      <Modal
        show={deleteTransfer}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static"
        centered
      >
        <Modal.Header
          closeButton
          onHide={() => {
            setDeleteTransfer(false);
            setDisappearPid('');
          }}
        >
          <Modal.Title id="contained-modal-title-vcenter">
            確認為本校生
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="overflow-auto">
          <span>學生身分證：</span>
          <FormInput.Control
            placeholder="輸入學生身分證"
            value={disappearPid}
            className="my-2"
            onChange={(e) => setDisappearPid(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="mx-2"
            onClick={() => {
              updateStudentTransferDone();
            }}
          >
            確認
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={deleteOtherSchooldData}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static"
        centered
      >
        <Modal.Header
          closeButton
          onHide={() => {
            setDeleteOtherSchooldData(false);
            setDisappearPid('');
          }}
        >
          <Modal.Title id="contained-modal-title-vcenter">
            移除非本校資料
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="overflow-auto">
          <span>學生身分證：</span>
          <FormInput.Control
            placeholder="輸入學生身分證"
            value={disappearPid}
            className="my-2"
            onChange={(e) => setDisappearPid(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="mx-2"
            onClick={() => {
              updateOtherSchooldData();
            }}
          >
            確認
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={!!modalContent}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static"
        centered
      >
        <Modal.Header
          closeButton
          onHide={() => {
            setDeleteTransfer(false);
          }}
        >
          <Modal.Title id="contained-modal-title-vcenter">
            {modalTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">{modalContent}</Modal.Body>

        <Modal.Footer>
          <Button
            onClick={() => {
              setModalContent(undefined);
            }}
          >
            關閉
          </Button>
        </Modal.Footer>
      </Modal>
    </AuthedLayout>
  );
};

export const StudentFindByName = connector(StudentSearchPage);
